import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Box,
} from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import { isRangeType } from "../../../lib/hatimType";
import {
  useUpdateMyHatimProgressMutation,
  useUpdateMyCompletedPageRangesMutation,
} from "../../../api/hatimApi";
import PageRangeEditor from "./PageRangeEditor";
import { SoftButton, useDialogPaperSx } from "../soft/SoftKit";

function isRowValid(row, maxPage) {
  const s = Number(row.startPage);
  const e = Number(row.endPage);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
  if (s < 1 || e < 1 || s > e) return false;
  if (maxPage > 0 && (s > maxPage || e > maxPage)) return false;
  return true;
}

function initialRows(instance) {
  const a = instance?.myAssignment;
  if (!a) return [];
  const arr = Array.isArray(a.completedPageRanges) ? a.completedPageRanges : [];
  return arr.map((r) => ({ startPage: String(r.startPage), endPage: String(r.endPage) }));
}

export default function UpdateHatimProgressModal({ open, instance, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const paperSx = useDialogPaperSx();
  const [updateAmount, { isLoading: amountLoading }] = useUpdateMyHatimProgressMutation();
  const [updateRanges, { isLoading: rangesLoading }] = useUpdateMyCompletedPageRangesMutation();

  const isRange = isRangeType(instance?.type);
  const [amount, setAmount] = useState(
    String(instance?.myAssignment?.completedAmount ?? "")
  );
  const [rows, setRows] = useState(initialRows(instance));

  useEffect(() => {
    if (open) {
      setAmount(String(instance?.myAssignment?.completedAmount ?? ""));
      setRows(initialRows(instance));
    }
  }, [open, instance]);

  const isLoading = amountLoading || rangesLoading;
  const maxPage = Number(instance?.totalAmount || 0);

  const amountNum = Number(amount);
  const amountValid =
    String(amount).trim() !== "" && Number.isFinite(amountNum) && amountNum >= 0;
  const rangesValid = rows.length === 0 || rows.every((r) => isRowValid(r, maxPage));
  const canSubmit = isRange ? rangesValid && !isLoading : amountValid && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit || !instance) return;
    try {
      if (isRange) {
        const cleanRanges = rows
          .filter((r) => isRowValid(r, maxPage))
          .map((r) => ({ startPage: Number(r.startPage), endPage: Number(r.endPage) }));
        await updateRanges({ instanceId: instance.id, ranges: cleanRanges }).unwrap();
      } else {
        await updateAmount({
          instanceId: instance.id,
          completedAmount: amountNum,
        }).unwrap();
      }
      toast.success(t("hatim_progress_save_success"));
      onClose && onClose();
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("hatim_progress_save_error");
      toast.error(msg);
    }
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={{ fontWeight: 500 }}>
        {t("hatim_progress_modal_title")}
      </DialogTitle>
      <DialogContent>
        {instance ? (
          <Typography variant="subtitle2" sx={{ fontWeight: 500, opacity: 0.8 }} mb={2}>
            {instance.name}
          </Typography>
        ) : null}

        {isRange ? (
          <PageRangeEditor
            assignedRanges={instance?.myAssignment?.assignedPageRanges}
            rows={rows}
            onChange={setRows}
            maxPage={maxPage}
          />
        ) : (
          <Box>
            <TextField
              type="number"
              fullWidth
              label={t("hatim_progress_modal_amount_label")}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{
                min: 0,
                max: instance?.myAssignment?.assignedAmount || undefined,
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SoftButton tone="cancel" onClick={onClose} disabled={isLoading}>
          {t("hatim_progress_cancel")}
        </SoftButton>
        <SoftButton tone="success" onClick={handleSubmit} disabled={!canSubmit}>
          {t("hatim_progress_save")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
